import { mergeAttributes, Node, ReactNodeViewRenderer, type Extensions } from '@tiptap/react'

import { messages } from '@/lib/messages'

import { ConditionNodeView, InlineRefView, RepeatNodeView, WidgetNodeView } from './node-views'

/*
 * Özel belge düğümleri. Ad ve öznitelikler `lib/report-document.ts` şemasıyla BİREBİR:
 * editör JSON'u = şema JSON'u. Öznitelikler varsayılansız (`undefined`) tanımlıdır; yazılmamış
 * öznitelik JSON'a çıkmaz (`getDocumentJSON` tanımsızları atar), şema varsayılanları uygular.
 * HTML (kopyala/yapıştır) gösterimi öznitelik başına JSON'dur (`data-*`).
 */

const t = messages.documents.nodes

const kebab = (name: string) => name.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)

/** Her öznitelik: varsayılan yok, HTML'de `data-<ad>` içinde JSON. */
function jsonAttrs(names: string[], defaults: Record<string, unknown> = {}) {
  return Object.fromEntries(
    names.map((name) => [
      name,
      {
        default: defaults[name],
        parseHTML: (element: HTMLElement) => {
          const raw = element.getAttribute(`data-${kebab(name)}`)
          if (raw === null) return undefined
          try {
            return JSON.parse(raw) as unknown
          } catch {
            return undefined
          }
        },
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes[name] === undefined
            ? {}
            : { [`data-${kebab(name)}`]: JSON.stringify(attributes[name]) },
      },
    ]),
  )
}

/** `widgetSchema` ortak öznitelikleri (`cube-report.ts` `widgetBase`) + `view`. */
const WIDGET_BASE = ['view', 'title', 'span', 'ignoreFilters', 'crossFilter']

function widgetNode(name: string, type: string, own: string[]) {
  return Node.create({
    name,
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    addAttributes: () => jsonAttrs([...WIDGET_BASE, ...own]),
    parseHTML: () => [{ tag: `div[data-type="${type}"]` }],
    renderHTML: ({ HTMLAttributes }) => [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': type }),
    ],
    addNodeView: () => ReactNodeViewRenderer(WidgetNodeView),
  })
}

export const ReportKpi = widgetNode('reportKpi', 'report-kpi', ['measure', 'fx'])
export const ReportTable = widgetNode('reportTable', 'report-table', [
  'dimensions',
  'measures',
  'where',
  'order',
  'totals',
  'pivot',
])
export const ReportList = widgetNode('reportList', 'report-list', [
  'columns',
  'rowKey',
  'defaultSort',
  'sortable',
  'totals',
  'where',
  'limit',
])
export const ReportChart = widgetNode('reportChart', 'report-chart', [
  'time',
  'granularity',
  'series',
  'horizontal',
  'range',
])

function inlineRefNode(name: 'value' | 'change') {
  return Node.create({
    name,
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    addAttributes: () => jsonAttrs(['of']),
    parseHTML: () => [{ tag: `span[data-type="${name}"]` }],
    renderHTML: ({ HTMLAttributes }) => [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-type': name }),
    ],
    addNodeView: () => ReactNodeViewRenderer(InlineRefView, { as: 'span' }),
  })
}

export const ValueNode = inlineRefNode('value')
export const ChangeNode = inlineRefNode('change')

const BRANCH_CLASS =
  'border-border before:text-muted-foreground relative my-1 rounded-sm border border-dashed px-3 py-2 before:mb-1 before:block before:text-xs before:content-[attr(data-label)]'

export const ConditionNode = Node.create({
  name: 'condition',
  group: 'block',
  content: 'conditionThen conditionElse',
  defining: true,
  isolating: true,
  addAttributes: () => jsonAttrs(['left', 'op', 'right']),
  parseHTML: () => [{ tag: 'div[data-type="condition"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-type': 'condition' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(ConditionNodeView),
})

export const ConditionThen = Node.create({
  name: 'conditionThen',
  content: 'block+',
  defining: true,
  isolating: true,
  parseHTML: () => [{ tag: 'div[data-branch="then"]' }],
  renderHTML: () => [
    'div',
    {
      'data-branch': 'then',
      'data-label': t.condition.then,
      class: `${BRANCH_CLASS} group-data-[active=then]/cond:border-primary group-data-[active=then]/cond:bg-accent`,
    },
    0,
  ],
})

export const ConditionElse = Node.create({
  name: 'conditionElse',
  content: 'block+',
  defining: true,
  isolating: true,
  parseHTML: () => [{ tag: 'div[data-branch="else"]' }],
  renderHTML: () => [
    'div',
    {
      'data-branch': 'else',
      'data-label': t.condition.else,
      class: `${BRANCH_CLASS} group-data-[active=else]/cond:border-primary group-data-[active=else]/cond:bg-accent`,
    },
    0,
  ],
})

export const RepeatNode = Node.create({
  name: 'repeat',
  group: 'block',
  content: 'block+',
  defining: true,
  isolating: true,
  addAttributes: () => jsonAttrs(['over', 'limit'], { limit: 50 }),
  parseHTML: () => [{ tag: 'div[data-type="repeat"]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'div',
    mergeAttributes(HTMLAttributes, { 'data-type': 'repeat' }),
    0,
  ],
  addNodeView: () => ReactNodeViewRenderer(RepeatNodeView),
})

export const PageBreak = Node.create({
  name: 'pagebreak',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  parseHTML: () => [{ tag: 'div[data-type="pagebreak"]' }],
  renderHTML: () => [
    'div',
    {
      'data-type': 'pagebreak',
      'data-label': t.pagebreak,
      class:
        'border-border text-muted-foreground my-4 border-t border-dashed pt-1 text-xs select-none before:content-[attr(data-label)]',
    },
  ],
})

const NOTE_CLASS = {
  info: 'border-primary/40',
  warning: 'border-warning/50',
} as const

export const NoteNode = Node.create({
  name: 'note',
  group: 'block',
  // Yalnız metin blokları: widget ve koşul notun içine girmez (şema).
  content: '(paragraph | bulletList | orderedList)+',
  defining: true,
  addAttributes: () => ({
    type: {
      default: 'info',
      rendered: false,
      parseHTML: (element: HTMLElement) =>
        element.getAttribute('data-note') === 'warning' ? 'warning' : 'info',
    },
  }),
  parseHTML: () => [{ tag: 'div[data-note]' }],
  renderHTML: ({ node }) => {
    const type = node.attrs.type === 'warning' ? 'warning' : 'info'
    return [
      'div',
      {
        'data-note': type,
        'data-label': t.note[type],
        class: `bg-surface-subtle my-3 rounded-sm border px-3 py-2 before:text-muted-foreground before:mb-1 before:block before:text-xs before:content-[attr(data-label)] ${NOTE_CLASS[type]}`,
      },
      0,
    ]
  },
})

/** Editöre eklenen özel düğümler (yerleşik düğümler `editor/extensions.ts`'te). */
export const documentNodeExtensions: Extensions = [
  ReportKpi,
  ReportTable,
  ReportList,
  ReportChart,
  ValueNode,
  ChangeNode,
  ConditionNode,
  ConditionThen,
  ConditionElse,
  RepeatNode,
  PageBreak,
  NoteNode,
]
